/**
 * src/pages/AssistantShifts.tsx
 * アシスタント自分専用：確定シフト確認ページ（閲覧のみ）
 *
 * - 自分の確定シフトだけ表示
 * - カレンダー風チップ表示
 * - 管理者メモがあれば表示
 * - 日数・概算給与を表示
 */

import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/client'
import { useAuth } from '../components/AuthGuard'
import { useAssistantMonth } from '../hooks/useAssistantMonth'
import { useIsMobile } from '../hooks/useIsMobile'

const DOW_LABEL = ['日', '月', '火', '水', '木', '金', '土']

function getDow(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).getDay()
}

function fmt(n: number) { return n.toLocaleString('ja-JP') }

type ShiftData = {
  month: string
  assistant_id: string
  assistant_name?: string
  confirmed_dates: string[]
  note: string | null
  days_count: number
  daily_rate?: number
  estimated_pay?: number
}

// ================================================================
// カレンダーチップ表示（クリック不可の読み取り専用）
// ================================================================
function ShiftCalendar({ month, confirmedDates }: { month: string; confirmedDates: string[] }) {
  const [y, m] = month.split('-').map(Number)
  const daysInMonth = new Date(y, m, 0).getDate()
  const confirmed = new Set(confirmedDates)

  const allDays = Array.from({ length: daysInMonth }, (_, i) => {
    return `${month}-${String(i + 1).padStart(2, '0')}`
  })

  // 週ごとにグループ化して曜日を揃える
  const weeks: string[][] = []
  let week: string[] = []
  allDays.forEach((date, idx) => {
    if (idx === 0) {
      // 最初の週：月曜始まりに空白で埋める
      const dow = getDow(date)
      const offset = dow === 0 ? 6 : dow - 1  // 月始まり（0=月, 6=日）
      for (let i = 0; i < offset; i++) week.push('')
    }
    week.push(date)
    if (getDow(date) === 0 || idx === allDays.length - 1) {
      weeks.push(week)
      week = []
    }
  })
  if (week.length > 0) weeks.push(week)

  return (
    <div>
      {/* 曜日ヘッダー（月〜日） */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
        {['月', '火', '水', '木', '金', '土', '日'].map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 10, fontWeight: 600,
            color: i === 5 ? '#2980b9' : i === 6 ? '#c0392b' : 'var(--ink-3)',
            paddingBottom: 4,
          }}>{d}</div>
        ))}
      </div>

      {/* 日付チップ */}
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
          {/* 7マスになるよう末尾を空白で埋める */}
          {[...week, ...Array(Math.max(0, 7 - week.length)).fill('')].map((date, di) => {
            if (!date) return <div key={di} />

            const dow    = getDow(date)
            const isSun  = dow === 0
            const isSat  = dow === 6
            const isConf = confirmed.has(date)
            const day    = Number(date.slice(8))

            let bg    = isSun ? '#fff5f5' : isSat ? '#f0f6ff' : 'var(--paper-2)'
            let color = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--ink-3)'
            let border = `1px solid ${isSun ? '#f5c6c2' : isSat ? '#bfd4f0' : 'transparent'}`

            if (isConf) {
              bg     = 'var(--green)'
              color  = '#fff'
              border = '1px solid transparent'
            }

            return (
              <div key={date} style={{
                textAlign: 'center', borderRadius: 5, padding: '6px 2px',
                background: bg, color, border,
                fontSize: 13, fontWeight: isConf ? 700 : 400,
                fontFamily: 'var(--font-mono)',
              }}>
                {day}
              </div>
            )
          })}
        </div>
      ))}

      {/* 凡例 */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: 11, color: 'var(--ink-3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--green)', display: 'inline-block' }} />
          確定出勤
        </span>
        <span style={{ color: '#c0392b' }}>日=日曜</span>
        <span style={{ color: '#2980b9' }}>土=土曜</span>
      </div>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function AssistantShifts() {
  const { user }  = useAuth()
  const isMobile  = useIsMobile()

  const [month, setMonth]      = useAssistantMonth(user?.assistant_id)
  const [data,     setData]       = useState<ShiftData | null>(null)
  const [loading,  setLoading]    = useState(true)
  const [notFound, setNotFound]   = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setNotFound(false)
    try {
      const result = await api.my.shifts.get(month)
      setData(result)
      setNotFound(result.days_count === 0 && result.confirmed_dates.length === 0)
    } catch {
      setData(null)
    } finally { setLoading(false) }
  }, [month])

  useEffect(() => { load() }, [load])

  return (
    <div>
      {/* 月選択 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input type="month" value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 14, width: 150 }} />
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{user?.assistant_name ?? ''}</span>
      </div>

      {loading ? (
        <div className="loading" style={{ padding: 48 }}>LOADING...</div>
      ) : (
        <>
          {/* サマリーカード */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="metric" style={{ minWidth: 100, padding: '10px 14px' }}>
              <div className="metric-label">確定日数</div>
              <div className={`metric-value ${(data?.days_count ?? 0) > 0 ? 'green' : ''}`}>
                {data?.days_count ?? 0}<span style={{ fontSize: 14, fontWeight: 400 }}>日</span>
              </div>
            </div>
            {(data?.daily_rate ?? 0) > 0 && (
              <div className="metric" style={{ minWidth: 130, padding: '10px 14px' }}>
                <div className="metric-label">概算給与</div>
                <div className="metric-value">
                  ¥{fmt(data?.estimated_pay ?? 0)}
                </div>
                <div className="metric-sub">日給×確定日数（交通費除く）</div>
              </div>
            )}
          </div>

          {/* 管理者メモ */}
          {data?.note && (
            <div style={{
              background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              fontSize: 13, color: 'var(--amber)',
            }}>
              📋 <strong>管理者メモ：</strong>{data.note}
            </div>
          )}

          {/* カレンダー or 日付リスト */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">確定シフト — {month}</span>
            </div>

            {notFound || !data || data.confirmed_dates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)', fontSize: 13 }}>
                {month} の確定シフトはまだ登録されていません
              </div>
            ) : (
              <div style={{ padding: isMobile ? '16px 14px' : '20px 24px' }}>
                {/* カレンダー表示 */}
                <ShiftCalendar month={month} confirmedDates={data.confirmed_dates} />

                {/* 日付リスト */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8 }}>
                    確定出勤日一覧
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[...data.confirmed_dates].sort().map((date) => {
                      const dow = getDow(date)
                      const isSun = dow === 0
                      const isSat = dow === 6
                      return (
                        <span key={date} style={{
                          fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500,
                          background: 'var(--green-light)', color: 'var(--green)',
                          borderRadius: 4, padding: '3px 8px',
                          border: '1px solid rgba(45,122,58,0.2)',
                        }}>
                          {date.slice(5).replace('-', '/')}
                          <span style={{
                            fontSize: 10, marginLeft: 3,
                            color: isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--green)',
                          }}>
                            （{DOW_LABEL[dow]}）
                          </span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, paddingLeft: 2 }}>
            ※ 確定シフトは管理者が設定します。変更が必要な場合は管理者にお問い合わせください。
          </div>
        </>
      )}
    </div>
  )
}